<template>
  <div
    class="deal-workflow-node"
    :class="[
      `deal-workflow-node--${kindVariant(item.kind)}`,
      directionClass,
      { 'deal-workflow-node--child': isChild },
    ]"
    :style="depthStyle"
  >
    <div class="d-flex flex-wrap align-start">
      <span
        class="deal-workflow-node__icon mr-3"
        :class="[
          `deal-workflow-node__icon--${kindVariant(item.kind)}`,
          directionClass && `deal-workflow-node__icon--${item.direction}`,
        ]"
      >
        <v-icon small>{{ kindIcon(item.kind) }}</v-icon>
      </span>

      <div class="deal-workflow-node__content flex-grow-1 min-width-0">
        <div class="d-flex flex-wrap align-center">
          <v-chip
            x-small
            class="mr-2 deal-workflow-node__kind-chip"
            :class="`deal-workflow-node__kind-chip--${kindVariant(item.kind)}`"
          >
            {{ kindLabel(item.kind) }}
          </v-chip>
          <v-chip
            v-if="directionLabel"
            x-small
            outlined
            class="mr-2 deal-workflow-node__direction"
            :class="`deal-workflow-node__direction--${item.direction}`"
          >
            {{ directionLabel }}
          </v-chip>
          <router-link :to="nodeRoute(item)" class="font-weight-medium mr-2">
            {{ item.code }}
          </router-link>
          <v-chip x-small>{{ item.status_label }}</v-chip>

          <template v-if="showProgress">
            <v-progress-linear
              :value="item.progress.percent"
              height="6"
              rounded
              class="deal-workflow-node__progress ml-2"
              color="primary"
            />
            <span class="text-caption grey--text ml-2">{{ item.progress.percent }}%</span>
          </template>

          <v-spacer v-if="showActions" />

          <slot v-if="showActions" name="actions" />
        </div>

        <div class="text-caption grey--text mt-1">{{ item.summary }}</div>

        <div v-if="plainReferences.length" class="deal-workflow-node__refs">
          <span class="deal-workflow-node__refs-label">{{ $t("DEALS.VIEW.LINKED_TO") }}:</span>
          <span
            v-for="(ref, refIndex) in plainReferences"
            :key="`${ref.kind}-${ref.id}`"
            class="deal-workflow-node__ref"
          >
            <v-icon
              x-small
              class="deal-workflow-node__ref-icon"
              :class="`deal-workflow-node__ref-icon--${kindVariant(ref.kind)}`"
            >
              {{ kindIcon(ref.kind) }}
            </v-icon>
            <router-link :to="nodeRoute(ref)">{{ ref.code }}</router-link>
            <span v-if="refIndex < plainReferences.length - 1" class="deal-workflow-node__ref-sep">,</span>
          </span>
        </div>

        <div v-if="isDeliveryNote" class="deal-workflow-node__links">
          <div
            v-for="link in linkSlots"
            :key="link.kind"
            class="deal-workflow-node__link"
            :class="[
              `deal-workflow-node__link--${kindVariant(link.kind)}`,
              { 'deal-workflow-node__link--filled': link.refs.length || (link.kind === 'invoice' && canCreateInvoice) },
            ]"
          >
            <span class="deal-workflow-node__link-label">{{ kindLabel(link.kind) }}</span>
            <div class="deal-workflow-node__link-body">
              <router-link
                v-for="ref in link.refs"
                :key="`${ref.kind}-${ref.id}`"
                :to="nodeRoute(ref)"
                class="deal-workflow-node__link-value"
              >
                {{ ref.code }}
              </router-link>
              <v-btn
                v-if="!link.refs.length && link.kind === 'invoice' && canCreateInvoice"
                x-small
                depressed
                color="primary"
                class="deal-workflow-node__link-action"
                @click="$emit('create-invoice')"
              >
                <v-icon x-small left>mdi-plus</v-icon>
                {{ invoiceActionLabel }}
              </v-btn>
              <span v-else-if="!link.refs.length" class="deal-workflow-node__link-empty">—</span>
            </div>
          </div>
        </div>

        <DealWorkflowFiles
          v-if="item.file_count"
          class="mt-2"
          :files="item.files"
          :downloading-file-id="downloadingFileId"
          @download="$emit('download-file', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DealWorkflowFiles from "@/views/pages/deals/partials/DealWorkflowFiles.vue";
import {
  kindIcon,
  kindLabel,
  kindVariant,
  LINK_SLOTS,
  nodeRoute,
} from "@/views/pages/deals/dealWorkflowMeta";

export default {
  name: "DealWorkflowNode",
  components: { DealWorkflowFiles },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isChild: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showProgress: {
      type: Boolean,
      default: false,
    },
    downloadingFileId: {
      type: [Number, String],
      default: null,
    },
    depth: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    depthStyle() {
      if (!this.isChild || this.depth <= 0) {
        return null;
      }
      return {
        marginLeft: `${18 * this.depth}px`,
      };
    },
    isDeliveryNote() {
      return this.item.kind === "delivery_note";
    },
    directionClass() {
      if (!this.isDeliveryNote) {
        return "";
      }
      return this.item.direction === "inbound" || this.item.direction === "outbound"
        ? `deal-workflow-node--${this.item.direction}`
        : "";
    },
    directionLabel() {
      if (!this.directionClass) {
        return "";
      }
      const labels = {
        outbound: this.$t("DEALS.VIEW.OUTBOUND"),
        inbound: this.$t("DEALS.VIEW.INBOUND"),
      };
      return labels[this.item.direction] || "";
    },
    plainReferences() {
      if (this.isDeliveryNote) {
        return [];
      }
      return Array.isArray(this.item.references) ? this.item.references : [];
    },
    linkSlots() {
      if (!this.isDeliveryNote) {
        return [];
      }
      const references = Array.isArray(this.item.references) ? this.item.references : [];
      return LINK_SLOTS.map((kind) => ({
        kind,
        refs: references.filter((ref) => ref.kind === kind),
      }));
    },
    canCreateInvoice() {
      const actions = this.item.actions || {};
      return this.item.kind === "delivery_note"
        && !!actions.can_create_invoice
        && this.hasPermissions("stock-invoices-create");
    },
    invoiceActionLabel() {
      if (this.item.direction === "inbound") {
        return this.$t("STOCK.DELIVERY_NOTES.RECORD_INVOICE");
      }
      return this.$t("STOCK.DELIVERY_NOTES.ISSUE_INVOICE");
    },
  },
  methods: {
    kindIcon,
    kindLabel,
    kindVariant,
    nodeRoute,
  },
};
</script>

<style scoped>
.deal-workflow-node {
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.deal-workflow-node--child {
  border-left: 3px solid #e3e6ef;
  padding-left: 16px;
}

.deal-workflow-node--child.deal-workflow-node--sales-order {
  border-left-color: #00897b;
}

.deal-workflow-node--child.deal-workflow-node--purchase-order {
  border-left-color: #3949ab;
}

.deal-workflow-node--child.deal-workflow-node--receipt {
  border-left-color: #f57c00;
}

.deal-workflow-node--child.deal-workflow-node--delivery {
  border-left-color: #7b1fa2;
}

.deal-workflow-node--child.deal-workflow-node--delivery-note {
  border-left-color: #00695c;
}

.deal-workflow-node--child.deal-workflow-node--outbound {
  border-left-color: #00897b;
}

.deal-workflow-node--child.deal-workflow-node--inbound {
  border-left-color: #3949ab;
}

.deal-workflow-node--child.deal-workflow-node--transport {
  border-left-color: #0288d1;
}

.deal-workflow-node__content {
  min-width: 0;
}

.deal-workflow-node__progress {
  max-width: 120px;
  min-width: 80px;
}

.deal-workflow-node__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.deal-workflow-node__icon--purchase-order {
  background: rgba(57, 73, 171, 0.1);
  color: #3949ab;
}

.deal-workflow-node__icon--sales-order {
  background: rgba(0, 137, 123, 0.1);
  color: #00897b;
}

.deal-workflow-node__icon--receipt {
  background: rgba(245, 124, 0, 0.1);
  color: #f57c00;
}

.deal-workflow-node__icon--delivery {
  background: rgba(123, 31, 162, 0.1);
  color: #7b1fa2;
}

.deal-workflow-node__icon--delivery-note {
  background: rgba(0, 105, 92, 0.1);
  color: #00695c;
}

.deal-workflow-node__icon--outbound {
  background: rgba(0, 137, 123, 0.12);
  color: #00897b;
}

.deal-workflow-node__icon--inbound {
  background: rgba(57, 73, 171, 0.12);
  color: #3949ab;
}

.deal-workflow-node__direction--outbound {
  border-color: #00897b !important;
  color: #00897b !important;
}

.deal-workflow-node__direction--inbound {
  border-color: #3949ab !important;
  color: #3949ab !important;
}

.deal-workflow-node__icon--transport {
  background: rgba(2, 136, 209, 0.1);
  color: #0288d1;
}

.deal-workflow-node__icon .v-icon {
  color: inherit !important;
}

.deal-workflow-node__kind-chip {
  font-weight: 600;
}

.deal-workflow-node__kind-chip--purchase-order {
  background: rgba(57, 73, 171, 0.12) !important;
  color: #3949ab !important;
}

.deal-workflow-node__kind-chip--sales-order {
  background: rgba(0, 137, 123, 0.12) !important;
  color: #00897b !important;
}

.deal-workflow-node__kind-chip--receipt {
  background: rgba(245, 124, 0, 0.12) !important;
  color: #f57c00 !important;
}

.deal-workflow-node__kind-chip--delivery {
  background: rgba(123, 31, 162, 0.12) !important;
  color: #7b1fa2 !important;
}

.deal-workflow-node__kind-chip--delivery-note {
  background: rgba(0, 105, 92, 0.12) !important;
  color: #00695c !important;
}

.deal-workflow-node__kind-chip--transport {
  background: rgba(2, 136, 209, 0.12) !important;
  color: #0288d1 !important;
}

.deal-workflow-node__kind-chip--invoice {
  background: rgba(93, 64, 55, 0.12) !important;
  color: #5d4037 !important;
}

.deal-workflow-node__icon--invoice {
  background: rgba(93, 64, 55, 0.1);
  color: #5d4037;
}

.deal-workflow-node__links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.deal-workflow-node__link {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 132px;
  min-height: 28px;
  max-width: 100%;
  padding: 3px 8px;
  border: 1px dashed #d7dbe6;
  border-radius: 6px;
  background: #f7f8fb;
}

.deal-workflow-node__link--filled {
  border-style: solid;
  background: #fff;
}

.deal-workflow-node__link-label {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
}

.deal-workflow-node__link--sales-order .deal-workflow-node__link-label {
  color: #00897b;
}

.deal-workflow-node__link--purchase-order .deal-workflow-node__link-label {
  color: #3949ab;
}

.deal-workflow-node__link--delivery-note .deal-workflow-node__link-label {
  color: #00695c;
}

.deal-workflow-node__link--invoice .deal-workflow-node__link-label {
  color: #5d4037;
}

.deal-workflow-node__link--filled.deal-workflow-node__link--sales-order {
  border-color: rgba(0, 137, 123, 0.45);
}

.deal-workflow-node__link--filled.deal-workflow-node__link--purchase-order {
  border-color: rgba(57, 73, 171, 0.45);
}

.deal-workflow-node__link--filled.deal-workflow-node__link--delivery-note {
  border-color: rgba(0, 105, 92, 0.45);
}

.deal-workflow-node__link--filled.deal-workflow-node__link--invoice {
  border-color: rgba(93, 64, 55, 0.45);
}

.deal-workflow-node__link-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.deal-workflow-node__link-value {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.deal-workflow-node__link-empty {
  color: #b0b6c4;
  font-size: 12px;
  line-height: 1;
}

.deal-workflow-node__link-action {
  height: 24px !important;
  min-width: 0 !important;
  padding: 0 8px !important;
  background-color: #1565c0 !important;
  color: #fff !important;
  font-size: 11px !important;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.deal-workflow-node__link-action .v-icon {
  color: #fff !important;
}

.deal-workflow-node__refs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
}

.deal-workflow-node__refs-label {
  margin-right: 4px;
  color: #9e9e9e;
}

.deal-workflow-node__ref {
  display: inline-flex;
  align-items: center;
  height: 16px;
}

.deal-workflow-node__ref-icon {
  width: 16px;
  height: 16px;
  margin-right: 2px;
  font-size: 14px !important;
}

.deal-workflow-node__ref-sep {
  margin: 0 4px 0 1px;
}

.deal-workflow-node__ref-icon--purchase-order {
  color: #3949ab !important;
}

.deal-workflow-node__ref-icon--sales-order {
  color: #00897b !important;
}

.deal-workflow-node__ref-icon--delivery-note {
  color: #00695c !important;
}

.deal-workflow-node__ref-icon--invoice {
  color: #5d4037 !important;
}

.deal-workflow-node__ref-icon--transport {
  color: #0288d1 !important;
}
</style>
